import { array, mixed, object, string } from "yup";
import { useForm, useFormState } from "react-hook-form";

import { PHONE_NUM_REGEX } from "@src/utility/helpers/regex";
import Validations from "@helpers/validations";
import useAxios from "@hooks/use-axios";
import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    name     : "",
    email    : "",
    password : "",
    platforms: [],
    role     : "",
    status   : "",
    title    : "",
    phone    : "",
    region   : {
        timezone: ""
    },
    tags: []
};

const useUpsert = props => {
    
    const {
        handleModalConfig,
        getUsers,
        modalConfig: { row },
        isEdit
    } = props;

    const { loading, axiosInstance } = useAxios({
        base: "panel"
    });

    const {
        requiredValidation,
        emailRequiredValidation,
        arrayMinMaxLengthValidation,
        regexValidation
    } = Validations();

    const validationSchema = object().shape({
        name     : requiredValidation({ name: "Name" }),
        email    : emailRequiredValidation({ name: "Email" }),
        role     : requiredValidation({ name: "Role" }),
        status   : requiredValidation({ name: "Status" }),
        title    : string(),
        password : mixed().nullable(),
        phone    : regexValidation({ regex: PHONE_NUM_REGEX, name: "Phone" }),
        platforms: arrayMinMaxLengthValidation({ name: "Platforms", min: 1 }),
        region   : object().shape({
            timezone: mixed().nullable()
        }),
        tags: array()
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    useEffect(() => {
        let isActive = true;
        if (isEdit && row.id && isActive) {
            reset({
                name     : row.name,
                email    : row.email,
                platforms: row.platforms?.map((item) => ({
                    label: item.name,
                    value: item._id
                })),
                role  : row.role,
                status: row.status,
                region: {
                    timezone: row.region.timezone
                },
                title: row.title,
                phone: row.phone,
                tags : row.tags?.filter(item => item.name).map((item) => ({ value: item._id, label: item.name }))
            });
        } else {
            reset(defaultValues);
        }
        return () => {
            isActive = false;
        };
    }, [isEdit, row]);

    const onSubmit = () => {
        handleSubmit(async data => {
            const payload = {
                name     : data.name,
                email    : data.email,
                password : data.password,
                platforms: data.platforms?.map((item) => item.value),
                role     : data.role,
                status   : data.status,
                title    : data.title,
                phone    : data.phone,
                region   : {
                    timezone: data.region.timezone
                },
                tags: data.tags?.map((item) => item.value)
            };

            if (row.id) {
                await axiosInstance.patch(
                    `/user/${row.id}`,
                    payload
                );
            } else {
                await axiosInstance.post("/user", payload);
            }
            handleModalConfig();
            getUsers();
        })();
    };

    return {
        onSubmit,
        methods,
        updateLoading: loading,
        isDirty
    };
};

export default useUpsert;
